import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Sidebar from "../../../sidebar";
import { MainContainer as AppShell, SideContainer } from "../styles";
import { BackgroundPattern } from "../../ListAreas/styles";
import FocusPointsSection from "./FocusPointsSection/FocusPointsSection.jsx";
import FocusPointsMap from "./FocusPointsSection/FocusPointsMap.jsx";
import { resolveFocusPoints } from "../../../../utils/alertFocusPoints.js";
import {
  AlertCard,
  NoDataContainer,
  HeaderSection,
  Title,
  InfoGrid,
  InfoSection,
  InfoItem,
  InfoLabel,
  InfoValue,
  LocationContainer,
  MapsLink,
  DetailsBody,
  MapContainer,
  MapsButtonContainer,
  InfoRow,
  InfoColumn,
  ReportContainer,
  ReportHeader,
  ReportTitle,
  ReportSubtitle,
  TimelineContainer,
  TicketCard,
  TicketHeader,
  StepBadge,
  StepNumber,
  DateBadge,
  TicketContent,
  ContentBlock,
  ContentLabel,
  ContentText,
  Image,
  ImageContainer,
  RejectButton,
  RejectButtonContainer,
  RegisterActionFab,
  StyledButtonVoltar,
  PhotoTypeBadge, ClickableImage, DocumentPreview, DocumentDetails, DocumentIcon, DocumentInfo, DocumentName, DocumentTypeLabel, DocumentDownloadLink,
  TeamSection,
  TeamSectionTitle,
  TeamGrid,
  TeamCard,
  TeamRole,
  TeamName,
  TeamContact,
  TeamEmpty,
} from './styles.js';
import { guardianAlertService } from "../../../../services/guardianAlertService.js";
import {
  ATTACHMENT_TYPE_ICONS,
  ATTACHMENT_TYPE_LABELS,
} from "../../../../utils/alertTicketAttachments.js";
import { RejectModal } from "../RejectModal/index.jsx";
import RichTextView from "../../../RichTextEditor/RichTextView.jsx";
import { buildAlertContactMailtoHref } from "../../../../utils/contactMailto.js";

const ROLE_LABELS = {
  vert_responsible: "Responsável VERT",
  adi_responsible: "Gestor",
  vert_team: "Equipe VERT",
  other_responsibles: "Equipe responsável",
  created_by: "Área criada por",
};

const collectAreaPeople = (areaResponsibles) => {
  if (!areaResponsibles || typeof areaResponsibles !== "object") return [];

  const people = [];
  const seen = new Set();

  const pushPerson = (roleKey, person) => {
    if (!person || (!person.full_name && !person.email && !person.phone)) return;
    const idKey =
      person.id != null
        ? String(person.id)
        : `${roleKey}:${person.email || person.full_name}`;
    if (seen.has(idKey)) return;
    seen.add(idKey);
    people.push({
      key: `${roleKey}-${idKey}`,
      role: ROLE_LABELS[roleKey] || roleKey,
      full_name: person.full_name || "Sem nome",
      email: person.email || "",
      phone: person.phone || "",
      user_type: person.user_type || "",
    });
  };

  pushPerson("vert_responsible", areaResponsibles.vert_responsible);
  pushPerson("adi_responsible", areaResponsibles.adi_responsible);
  (areaResponsibles.vert_team || []).forEach((p) => pushPerson("vert_team", p));
  (areaResponsibles.other_responsibles || []).forEach((p) =>
    pushPerson("other_responsibles", p)
  );
  pushPerson("created_by", areaResponsibles.created_by);

  return people;
};

export const AlertDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const collapsed = useSelector((state) => state.sidebar);
  const { ticketId, preloadedAlert, codAlerta } = location.state || {};
  const [tickets, setTickets] = useState([]);
  const [alert, setAlert] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rejectModal, setRejectModal] = useState({ open: false, ticketId: null, alertId: null });
  const [rejectionReason, setRejectionReason] = useState("");
  const [rejecting, setRejecting] = useState(false);
  // Estado para o modal de imagem
  const [modalImage, setModalImage] = useState(null);

  const areaWkts = useMemo(
    () => (alert?.fires_monitor_area || []).map((a) => a.polygon_wkt).filter(Boolean),
    [alert]
  );

  const ACTION_LABELS = {
    criado: "Alerta criado",
    recebido: "Recebido",
    aguardando_resposta: "Aguardando Resposta",
    resposta_rejeitado: "Resposta Rejeitada",
    equipe_enviada: "Equipe Enviada",
    respondido_processo_sucessao: "Respondido / Processo de Sucessão Natural",
    encerrado: "Encerrado",
  };

  const formatAction = (action) => ACTION_LABELS[action] ?? action;

  useEffect(() => {
    let cancelled = false;

    const applyAlertPayload = (payload) => {
      if (cancelled || !payload) return;
      setAlert(payload);
      setTickets(payload.tickets || []);
    };

    const fetchById = async (id) => {
      const response = await guardianAlertService.getTicketsFromSameAlert(id);
      applyAlertPayload(response);
    };

    const boot = async () => {
      setLoading(true);
      try {
        // Preferir fetch completo (traz area_responsibles) quando houver id
        if (ticketId) {
          await fetchById(ticketId);
          return;
        }

        if (preloadedAlert?.alert_id) {
          // preloaded sem ticket: tenta enriquecer via alert_id
          try {
            await fetchById(preloadedAlert.alert_id);
            return;
          } catch (err) {
            console.warn("Não foi possível enriquecer alerta pré-carregado:", err);
            applyAlertPayload(preloadedAlert);
            return;
          }
        }

        if (preloadedAlert) {
          applyAlertPayload(preloadedAlert);
        }
      } catch (err) {
        console.error("Erro ao buscar tickets do alerta:", err);
        if (preloadedAlert) applyAlertPayload(preloadedAlert);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    boot();
    return () => {
      cancelled = true;
    };
  }, [ticketId, preloadedAlert]);
  // Fechar modal com ESC
  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === 'Escape') {
        setModalImage(null);
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return 'Não informado';
    try {
      const date = new Date(dateString);
      return isNaN(date.getTime())
        ? 'Data inválida'
        : date.toLocaleDateString('pt-BR') + ' às ' + date.toLocaleTimeString('pt-BR');
    } catch (e) {
      return 'Data inválida';
    }
  };

  const getEmbedMapUrl = (coordinates) => {
    if (!coordinates || coordinates === '-') return null;

    const cleanCoords = coordinates.trim();
    const coordsArray = cleanCoords.split(',');

    // Se for um único ponto (lat,lng)
    if (coordsArray.length === 2) {
      const lat = coordsArray[0].trim();
      const lng = coordsArray[1].trim();
      return `https://maps.google.com/maps?q=${lat},${lng}&z=15&output=embed`;
    }

    // Fallback para pesquisa por texto
    return `https://maps.google.com/maps?q=${encodeURIComponent(cleanCoords)}&z=15&output=embed`;
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) return;

    setRejecting(true);
    try {
      await guardianAlertService.rejectAlertResponse({
        alert: rejectModal.alertId,
        rejection_reason: rejectionReason.trim(),
      });

      // Atualiza a lista de tickets após rejeição
      const response = await guardianAlertService.getTicketsFromSameAlert(ticketId);
      setAlert(response);
      setTickets(response.tickets);

      setRejectModal({ open: false, ticketId: null, alertId: null });
      setRejectionReason("");
    } catch (err) {
      console.error("Erro ao rejeitar resposta:", err);
    } finally {
      setRejecting(false);
    }
  };

  const handleCancel = () => {
    navigate("/view_alerts");
  };


  const getResponsibleName = (user) => {
    return user?.full_name || user?.name || user?.email || 'Não informado';
  };

  const buildMailtoHref = (email) =>
    buildAlertContactMailtoHref(email, alert, codAlerta);

  // Função auxiliar para montar o conteúdo do bloco "Equipe Enviada"
  const getTeamDisplayContent = (ticket) => {
    const sentTeam = ticket.sent_team || ticket.other_responsibles || [];
    const observations = ticket.observations || "";
    const hasOutro = observations.toLowerCase().includes("outro");

    let teamText = "";
    if (sentTeam.length > 0) {
      teamText = sentTeam.map((user) => getResponsibleName(user)).join(", ");
    }

    if (hasOutro) {
      if (teamText) {
        return `${teamText} — Observação: ${observations}`;
      }
      return observations;
    }

    return teamText || "Nenhuma equipe registrada";
  };

  const areaPeople = collectAreaPeople(alert?.area_responsibles);
  const focusPoints = resolveFocusPoints(alert);

  const isImageFile = (photo) => {
    if (photo.type === 'documento') return false;
    const url = photo.image_url;
    const ext = url.split('.').pop().toLowerCase();
    return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
  };

  // Extrai nome do arquivo da URL
  const getFileName = (url) => {
    return url.split('/').pop();
  };

  // Tipo de arquivo para exibição
  const getPhotoTypeLabel = (type) => {
    switch (type) {
      case "natural": return "Processo Natural";
      case "antropico": return "Processo Antrópico";
      case "intencional": return "Processo Intencional";
      case "documento": return "Documento anexado";
      default: return "Tipo não informado";
    }
  };

  const getAttachmentLabel = (attachment) => {
    const type = attachment?.attachment_type;
    return ATTACHMENT_TYPE_LABELS[type] || "Anexo";
  };

  const getAttachmentIcon = (attachment) => {
    const type = attachment?.attachment_type;
    return ATTACHMENT_TYPE_ICONS[type] || "📎";
  };

  const getAttachmentFileName = (attachment) =>
    attachment?.filename ||
    attachment?.original_filename ||
    getFileName(attachment?.file_url || "");


  // Se não há dados do alerta, mostra uma mensagem
  if (loading) {
    return (
      <AppShell>
        <BackgroundPattern />
        <Sidebar />
        <SideContainer collapsed={collapsed}>
          <DetailsBody $topPad>
            <NoDataContainer>
              <h3>Carregando alerta...</h3>
            </NoDataContainer>
          </DetailsBody>
        </SideContainer>
      </AppShell>
    );
  }

  if (!alert) {
    return (
      <AppShell>
        <BackgroundPattern />
        <Sidebar />
        <SideContainer collapsed={collapsed}>
          <DetailsBody $topPad>
            <NoDataContainer>
              <h3>Nenhum alerta selecionado</h3>
              <p>Por favor, selecione um alerta da lista para visualizar os detalhes.</p>
            </NoDataContainer>
          </DetailsBody>
        </SideContainer>
      </AppShell>
    );
  }

  const registerAlertId = alert.alert_id ?? tickets[0]?.alert_id;

  return (
    <AppShell>
      <BackgroundPattern />
      <Sidebar />
      <SideContainer collapsed={collapsed}>
        <DetailsBody $topPad>

          {/* BLOCO COM INFORMAÇÕES DO ALERTA */}
          {alert && (
            <AlertCard>
              <HeaderSection>
                <Title>Informações do Alerta</Title>
              </HeaderSection>
              <InfoGrid>
                <InfoSection borderColor="color-mix(in srgb, var(--info) 50%, transparent)">

                  {(alert.cod_alerta || codAlerta) ? (
                    <InfoRow>
                      <InfoColumn>
                        <InfoItem>
                          <InfoLabel>Código:</InfoLabel>
                          <InfoValue>{alert.cod_alerta || codAlerta}</InfoValue>
                        </InfoItem>
                      </InfoColumn>
                    </InfoRow>
                  ) : null}

                  {alert.alert_id ? (
                    <InfoRow>
                      <InfoColumn>
                        <InfoItem>
                          <InfoLabel>ID do alerta:</InfoLabel>
                          <InfoValue style={{ wordBreak: "break-all", fontSize: 13 }}>
                            {alert.alert_id}
                          </InfoValue>
                        </InfoItem>
                      </InfoColumn>
                      <InfoColumn>
                        <InfoItem>
                          <InfoLabel>Origem:</InfoLabel>
                          <InfoValue>
                            {alert.alert_request_id
                              ? "Gerado a partir de uma solicitação de alerta"
                              : "Criado manualmente (sem solicitação de origem)"}
                          </InfoValue>
                        </InfoItem>
                      </InfoColumn>
                    </InfoRow>
                  ) : null}

                  {/* Área */}
                  <InfoRow>
                    <InfoColumn>
                      <InfoItem>
                        <InfoLabel>Área:</InfoLabel>
                        <InfoValue>{alert.area_title || 'Não informado'}</InfoValue>
                      </InfoItem>
                    </InfoColumn>
                    {alert.project_title ? (
                      <InfoColumn>
                        <InfoItem>
                          <InfoLabel>Projeto:</InfoLabel>
                          <InfoValue>{alert.project_title}</InfoValue>
                        </InfoItem>
                      </InfoColumn>
                    ) : null}
                  </InfoRow>

                  {alert.alert_user ? (
                    <InfoRow>
                      <InfoColumn>
                        <InfoItem>
                          <InfoLabel>Alerta criado por:</InfoLabel>
                          <InfoValue>{alert.alert_user}</InfoValue>
                        </InfoItem>
                      </InfoColumn>
                    </InfoRow>
                  ) : null}

                  <TeamSection>
                    <TeamSectionTitle>Responsáveis pela área</TeamSectionTitle>
                    {areaPeople.length > 0 ? (
                      <TeamGrid>
                        {areaPeople.map((person) => (
                          <TeamCard key={person.key}>
                            <TeamRole>{person.role}</TeamRole>
                            <TeamName>{person.full_name}</TeamName>
                            {person.email ? (
                              <TeamContact
                                href={buildMailtoHref(person.email)}
                                title={`Enviar e-mail para ${person.full_name}`}
                              >
                                ✉️ {person.email}
                              </TeamContact>
                            ) : null}
                            {person.phone ? (
                              <TeamContact
                                href={`tel:${person.phone}`}
                                title={`Ligar para ${person.full_name}`}
                              >
                                📞 {person.phone}
                              </TeamContact>
                            ) : null}
                          </TeamCard>
                        ))}
                      </TeamGrid>
                    ) : (
                      <TeamEmpty>
                        Nenhum contato de responsável disponível para esta área.
                      </TeamEmpty>
                    )}
                  </TeamSection>

                  {/* Criado em */}
                  <InfoRow>
                    <InfoColumn>
                      <InfoItem>
                        <InfoLabel>Criado em:</InfoLabel>
                        <InfoValue>{formatDate(alert.alert_created_at)}</InfoValue>
                      </InfoItem>
                    </InfoColumn>
                  </InfoRow>

                  {/* Tipo de alerta + severidade */}
                  <InfoRow>
                    <InfoColumn>
                      <InfoItem>
                        <InfoLabel>Tipo de alerta:</InfoLabel>
                        <InfoValue>{alert.alert_type_description || 'Não informado'}</InfoValue>
                      </InfoItem>
                    </InfoColumn>

                    <InfoColumn>
                      <InfoItem>
                        <InfoLabel>Severidade:</InfoLabel>
                        <InfoValue>{alert.severity_level_description || 'Não informado'}</InfoValue>
                      </InfoItem>
                    </InfoColumn>
                  </InfoRow>

                  {/* Detalhes do alerta (texto rico) */}
                  <InfoItem>
                    <InfoLabel>Detalhes:</InfoLabel>
                    <InfoValue as="div">
                      <RichTextView
                        value={alert.alert_observation}
                        fallback="Nenhum detalhe registrado"
                      />
                    </InfoValue>
                  </InfoItem>

                  {alert.description ? (
                    <InfoItem>
                      <InfoLabel>Descrição:</InfoLabel>
                      <InfoValue>{alert.description}</InfoValue>
                    </InfoItem>
                  ) : null}



                  {/* Local / Coordenadas */}
                  <InfoItem>
                    <InfoLabel>Local:</InfoLabel>

                    {alert.coordinates && alert.coordinates !== '-' ? (
                      <LocationContainer>
                        {focusPoints.length > 0 ? (
                          <FocusPointsMap
                            points={focusPoints}
                            alertCoordinates={alert.coordinates}
                            areaWkts={areaWkts}
                          />
                        ) : (
                          getEmbedMapUrl(alert.coordinates) && (
                            <MapContainer>
                              <iframe
                                src={getEmbedMapUrl(alert.coordinates)}
                                allowFullScreen=""
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                                title="Localização do Alerta"
                              ></iframe>
                            </MapContainer>

                          )
                        )}

                        <MapsButtonContainer>
                          <MapsLink
                            href={`https://www.google.com/maps?q=${alert.coordinates}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            📍 Ver no Google Maps
                          </MapsLink>
                          {registerAlertId ? (
                            <RegisterActionFab
                              type="button"
                              onClick={() =>
                                navigate("/alert-register-action", {
                                  state: {
                                    alertId: registerAlertId,
                                    ticketId: ticketId || null,
                                  },
                                })
                              }
                            >
                              Registrar Ação Tomada
                            </RegisterActionFab>
                          ) : null}
                        </MapsButtonContainer>
                      </LocationContainer>
                    ) : (
                      <>
                        <InfoValue>Coordenadas não informadas</InfoValue>
                        {registerAlertId ? (
                          <MapsButtonContainer style={{ justifyContent: "flex-end" }}>
                            <RegisterActionFab
                              type="button"
                              onClick={() =>
                                navigate("/alert-register-action", {
                                  state: {
                                    alertId: registerAlertId,
                                    ticketId: ticketId || null,
                                  },
                                })
                              }
                            >
                              Registrar Ação Tomada
                            </RegisterActionFab>
                          </MapsButtonContainer>
                        ) : null}
                      </>
                    )}
                  </InfoItem>

                  <InfoItem>
                    <InfoLabel>Pontos de foco:</InfoLabel>
                    <FocusPointsSection points={focusPoints} />
                  </InfoItem>

                </InfoSection>
              </InfoGrid>
            </AlertCard>
          )}

          {/* ================================ */}
          {/*     BLOCOS DO RELATÓRIO           */}
          {/* ================================ */}
          {tickets.length > 0 && (
            <ReportContainer>
              <ReportHeader>
                <ReportTitle>Etapas de resposta Guardian</ReportTitle>
                <ReportSubtitle>Acompanhe as etapas dos tickets dos alertas, que indicam o estado de comunicação do servidor com a aplicação de celular do responsável pelo projeto (ponto focal).</ReportSubtitle>
                <ReportSubtitle></ReportSubtitle>
                <ReportSubtitle>{tickets.length} {tickets.length === 1 ? 'etapa registrada' : 'etapas registradas'}</ReportSubtitle>
              </ReportHeader>

              <TimelineContainer>
                {tickets
                  .sort((a, b) => new Date(a.ticket_created_at) - new Date(b.ticket_created_at))
                  .map((t, index) => (
                    <TicketCard
                      key={t.ticket_id}
                      borderColor="color-mix(in srgb, var(--warning) 50%, transparent)"
                    >
                      <TicketHeader>
                        <StepBadge>
                          <StepNumber>{index + 1}</StepNumber>
                          <span>{formatAction(t.action)}</span>
                        </StepBadge>
                        <DateBadge>📅 {formatDate(t.ticket_created_at)}</DateBadge>
                      </TicketHeader>

                      {/* Botão rejeitar (original) */}
                      {t.action === "respondido_processo_sucessao" && (
                        <RejectButtonContainer>
                          <RejectButton
                            onClick={() => setRejectModal({ open: true, ticketId: t.ticket_id, alertId: alert.alert_id })}
                          >
                            <svg width="14" height="14" viewBox="0 0 18 14" fill="none">
                              <path d="M2 2l10 10M12 2L2 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                            </svg> Rejeitar resposta
                          </RejectButton>
                        </RejectButtonContainer>
                      )}

                      <TicketContent>
                        {(t.action === "respondido_processo_sucessao" ||
                          t.action === "resposta_rejeitado") && (
                            <ContentBlock>
                              <ContentLabel>📝 Observações</ContentLabel>
                              <ContentText>
                                {t.observations?.trim() || "Nenhuma observação registrada"}
                              </ContentText>
                            </ContentBlock>
                          )}

                        <ContentBlock>
                          <ContentLabel>💬 Respostas</ContentLabel>
                          <ContentText>
                            {t.responses?.trim() || "Nenhuma resposta registrada"}
                          </ContentText>
                        </ContentBlock>

                        {t.action === "equipe_enviada" && (
                          <ContentBlock>
                            <ContentLabel>👥 Equipe Enviada</ContentLabel>
                            <ContentText>
                              {getTeamDisplayContent(t)}
                            </ContentText>
                          </ContentBlock>
                        )}

                        {Array.isArray(t.photos) && t.photos.length > 0 && (
                          <ImageContainer>
                            {t.photos.map((photo) => (
                              <div key={photo.id}>
                                {/* Badge do tipo */}
                                <PhotoTypeBadge $type={photo.type}>
                                  {getPhotoTypeLabel(photo.type)}
                                </PhotoTypeBadge>

                                {/* Conteúdo condicional */}
                                {isImageFile(photo) ? (
                                  <ClickableImage
                                    src={photo.image_url}
                                    alt="Imagem do ticket"
                                    onClick={() => setModalImage(photo.image_url)}
                                  />
                                ) : (
                                  <DocumentPreview>
                                    <DocumentDetails>
                                      <DocumentIcon>📄</DocumentIcon>
                                      <DocumentInfo>
                                        <DocumentName>{getFileName(photo.image_url)}</DocumentName>
                                        <DocumentTypeLabel>
                                          {photo.type === 'documento' ? 'Documento' : 'Arquivo'}
                                        </DocumentTypeLabel>
                                      </DocumentInfo>
                                    </DocumentDetails>
                                    <DocumentDownloadLink
                                      href={photo.image_url}
                                      download
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      Abrir Documento
                                    </DocumentDownloadLink>
                                  </DocumentPreview>
                                )}
                              </div>
                            ))}
                          </ImageContainer>
                        )}

                        {Array.isArray(t.attachments) && t.attachments.length > 0 && (
                          <ContentBlock>
                            <ContentLabel>📎 Anexos</ContentLabel>
                            {t.attachments.map((attachment) => (
                              <DocumentPreview key={attachment.id}>
                                <DocumentDetails>
                                  <DocumentIcon>
                                    {getAttachmentIcon(attachment)}
                                  </DocumentIcon>
                                  <DocumentInfo>
                                    <DocumentName>
                                      {getAttachmentFileName(attachment)}
                                    </DocumentName>
                                    <DocumentTypeLabel>
                                      {getAttachmentLabel(attachment)}
                                    </DocumentTypeLabel>
                                  </DocumentInfo>
                                </DocumentDetails>
                                {attachment.file_url ? (
                                  <DocumentDownloadLink
                                    href={attachment.file_url}
                                    download
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    Baixar
                                  </DocumentDownloadLink>
                                ) : null}
                              </DocumentPreview>
                            ))}
                          </ContentBlock>
                        )}
                      </TicketContent>
                    </TicketCard>
                  ))}
              </TimelineContainer>
            </ReportContainer>
          )}

          <StyledButtonVoltar onClick={handleCancel}>
            ← Voltar
          </StyledButtonVoltar>

          {/* fora do .map(), no final do return */}
          <RejectModal
            open={rejectModal.open}
            onClose={() => {
              setRejectModal({ open: false, ticketId: null, alertId: null });
              setRejectionReason("");
            }}
            onConfirm={handleReject}
            reason={rejectionReason}
            setReason={setRejectionReason}
            rejecting={rejecting}
          />


          {/* Modal de imagem - tamanho aproximado A4 */}
          {modalImage && (
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundColor: 'var(--surface-overlay)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 9999,
                backdropFilter: 'blur(5px)'
              }}
              onClick={() => setModalImage(null)}
            >
              <div
                style={{
                  position: 'relative',
                  maxWidth: '90vw',
                  maxHeight: '90vh',
                  width: 'auto',
                  height: 'auto',
                  backgroundColor: "var(--surface-1)",
                  borderRadius: '8px',
                  padding: '16px',
                  boxShadow: 'var(--shadow-lg)',
                  // Estilo para aproximar proporção A4 (1:√2)
                  // Ajuste para exibir bem a imagem, respeitando suas proporções
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center'
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  onClick={() => setModalImage(null)}
                  style={{
                    position: 'absolute',
                    top: '10px',
                    right: '20px',
                    background: 'none',
                    border: 'none',
                    fontSize: '28px',
                    cursor: 'pointer',
                    color: 'var(--text-primary)',
                    fontWeight: 'bold',
                    zIndex: 10
                  }}
                >
                  &times;
                </button>
                <img
                  src={modalImage}
                  alt="Ampliada"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '85vh',
                    objectFit: 'contain',
                    borderRadius: '4px'
                  }}
                />
              </div>
            </div>
          )}

        </DetailsBody>
      </SideContainer>
    </AppShell>
  );

};